<template>
    <div>
        <page-title
            :title="$t('deletedRecords.lecturers')"
            :showTitle="false"
        ></page-title>
        <div class="card">
            <div class="card-header bg-primary text-white">
                <div class="card-title h5 m-0">
                    {{ $t("deletedRecords.lecturers") }}
                </div>
            </div>

            <div class="card-body">
                <div
                    class="btn-toolbar justify-content-between mt-0 mb-2"
                    role="toolbar"
                >
                    <div class="form-inline col-6"></div>

                    <div class="input-group">
                        <input
                            v-model="searchQuery"
                            type="text"
                            v-on:keyup.enter.stop="massSearch"
                            v-on:keyup.esc.stop="clearFilters"
                            class="form-control"
                            aria-label="Mass search input"
                            aria-describedby="massSearch"
                        />
                        <button
                            class="btn btn-secondary"
                            @click.stop="massSearch"
                        >
                            <i class="fas fa-search"></i>
                            {{ $t("buttons.search.btnText") }}
                        </button>
                        <button
                            class="btn btn-secondary"
                            @click.stop="clearFilters"
                        >
                            <i class="fas fa-times"></i>
                            {{ $t("buttons.clearFilters.btnText") }}
                        </button>
                    </div>
                </div>

                <kendo-datasource
                    ref="datasource1"
                    :transport="transportSettings"
                    :schema-model-id="'id'"
                    :schema-model-fields="schemaModelFields"
                    :schema-total="'total'"
                    :schema-data="'data'"
                    :page-size="defaultGridRows"
                    :server-paging="true"
                    :server-sorting="true"
                    :server-filtering="true"
                    :error="dataSourceError"
                >
                </kendo-datasource>

                <kendo-grid
                    ref="grid"
                    :data-source-ref="'datasource1'"
                    :editable="gridOptions.editable"
                    :filterable="gridOptions.filterable"
                    :pageable="gridOptions.pageable"
                    :reorderable="gridOptions.reorderable"
                    :resizable="gridOptions.resizable"
                    :groupable="gridOptions.groupable"
                    :allow-copy="gridOptions.allowCopy"
                    :column-menu="gridOptions.columnMenu"
                    :sortable-mode="gridOptions.sortable.mode"
                    :sortable-allow-unsort="gridOptions.sortable.allowUnsort"
                    :sortable-show-indexes="gridOptions.sortable.showIndexes"
                    :selectable="gridOptions.selectable"
                    :scrollable="gridOptions.scrollable"
                >
                    <kendo-grid-column
                        :template="btnsTemplate"
                        field="btnsColumn"
                        :title="' '"
                        :reorderable="false"
                        :sortable="false"
                        :filterable="false"
                        :lockable="false"
                        :locked="false"
                        :width="80"
                    >
                    </kendo-grid-column>
                    <kendo-grid-column
                        :field="'id'"
                        :filterable="false"
                        :width="100"
                        :lockable="false"
                        :locked="false"
                        :hidden="true"
                        :title="$t('registers.lectors.id')"
                    >
                    </kendo-grid-column>
                    <kendo-grid-column
                        :field="'fullName'"
                        :width="300"
                        :lockable="true"
                        :locked="false"
                        :hidden="isEnVersion"
                        :title="$t('person.firstName')"
                    >
                    </kendo-grid-column>
                    <kendo-grid-column
                        :field="'fullNameEn'"
                        :width="300"
                        :lockable="true"
                        :locked="false"
                        :sortable="false"
                        :filterable="false"
                        :title="$t('person.gridHeaders.nameTransliterated')"
                    >
                    </kendo-grid-column>
                    <kendo-grid-column
                        :field="'position'"
                        :width="200"
                        :lockable="true"
                        :locked="false"
                        :hidden="isEnVersion"
                        :title="$t('person.position')"
                    >
                    </kendo-grid-column>
                    <kendo-grid-column
                        :field="'positionEn'"
                        :width="200"
                        :lockable="true"
                        :locked="false"
                        :hidden="!isEnVersion"
                        :title="$t('person.position')"
                    >
                    </kendo-grid-column>
                    <kendo-grid-column
                        :field="'email'"
                        :width="200"
                        :lockable="true"
                        :locked="false"
                        :title="$t('email')"
                    >
                    </kendo-grid-column>
                    <kendo-grid-column
                        :field="'status'"
                        :width="200"
                        :lockable="true"
                        :locked="false"
                        :hidden="isEnVersion"
                        :title="$t('registers.lectors.status')"
                    >
                    </kendo-grid-column>
                    <kendo-grid-column
                        :field="'statusEn'"
                        :width="200"
                        :lockable="true"
                        :locked="false"
                        :hidden="!isEnVersion"
                        :title="$t('registers.lectors.status')"
                    >
                    </kendo-grid-column>
                    <kendo-grid-column
                        :field="'thematicArea'"
                        :width="200"
                        :lockable="true"
                        :locked="false"
                        :hidden="isEnVersion"
                        :title="$t('registers.lectors.thematicArea')"
                    >
                    </kendo-grid-column>
                    <kendo-grid-column
                        :field="'thematicAreaEn'"
                        :width="200"
                        :lockable="true"
                        :locked="false"
                        :hidden="!isEnVersion"
                        :title="$t('registers.lectors.thematicArea')"
                    >
                    </kendo-grid-column>
                    <kendo-grid-column
                        :field="'institutionName'"
                        :width="200"
                        :lockable="true"
                        :locked="false"
                        :hidden="isEnVersion"
                        :title="$t('institution.name')"
                    >
                    </kendo-grid-column>
                    <kendo-grid-column
                        :field="'institutionNameEn'"
                        :width="200"
                        :lockable="true"
                        :locked="false"
                        :hidden="!isEnVersion"
                        :title="$t('institution.name')"
                    >
                    </kendo-grid-column>
                    <kendo-grid-column
                        :field="'phone'"
                        :width="200"
                        :lockable="true"
                        :locked="false"
                        :hidden="false"
                        :title="$t('person.phone')"
                    >
                    </kendo-grid-column>
                    <kendo-grid-column
                        :field="'mobilePhone'"
                        :width="200"
                        :lockable="true"
                        :locked="false"
                        :hidde="false"
                        :title="$t('person.mobilePhone')"
                    >
                    </kendo-grid-column>
                    <kendo-grid-column
                        :field="'institutionCity'"
                        :width="200"
                        :lockable="true"
                        :locked="false"
                        :hidden="isEnVersion"
                        :title="$t('address.settlement')"
                    >
                    </kendo-grid-column>
                    <kendo-grid-column
                        :field="'institutionCityEn'"
                        :width="200"
                        :lockable="true"
                        :locked="false"
                        :hidden="!isEnVersion"
                        :title="$t('address.settlement')"
                    >
                    </kendo-grid-column>
                    <kendo-grid-column
                        :field="'institutionCountry'"
                        :width="200"
                        :lockable="true"
                        :locked="false"
                        :hidden="isEnVersion"
                        :title="$t('address.country')"
                    >
                    </kendo-grid-column>
                    <kendo-grid-column
                        :field="'institutionCountryEn'"
                        :width="200"
                        :lockable="true"
                        :locked="false"
                        :hidden="!isEnVersion"
                        :title="$t('address.country')"
                    >
                    </kendo-grid-column>
                </kendo-grid>
            </div>
        </div>
    </div>
</template>

<script>
import Vue from "vue";
import store from "@/store/store";
import BtnsTemplate from "@/components/grid/StandardBtnsTemplate";
import PageTitle from "@/components/common/PageTitle";
import { mapGetters } from "vuex";

export default {
    components: {
        PageTitle,
    },
    data() {
        return {
            defaultGridRows: this.$store.getters.defaultGridRows,
            searchQuery: "",
            excludedColumnsForSearch: [],
            schemaModelFields: {
                id: {
                    name: "id",
                    type: "number",
                    editable: false,
                    nullable: true,
                    visible: true,
                    titleKey: "registers.lectors.id",
                },
                name: {
                    name: "name",
                    type: "string",
                    editable: false,
                    nullable: false,
                    visible: true,
                    titleKey: "person.firstName",
                },
                fullName: {
                    name: "fullName",
                    type: "string",
                    editable: false,
                    nullable: false,
                    visible: true,
                },
                nafullNameEnme: {
                    name: "fullNameEn",
                    type: "string",
                    editable: false,
                    nullable: false,
                    visible: true,
                },
                nameTransliterated: {
                    name: "nameTransliterated",
                    type: "string",
                    editable: false,
                    nullable: false,
                    visible: true,
                    titleKey: "person.gridHeaders.nameTransliterated",
                },
                position: {
                    name: "position",
                    type: "string",
                    editable: false,
                    nullable: true,
                    visible: true,
                    titleKey: "person.position",
                },
                positionEn: {
                    name: "positionEn",
                    type: "string",
                    editable: false,
                    nullable: true,
                    visible: true,
                    titleKey: "person.position",
                },
                email: {
                    name: "email",
                    type: "string",
                    editable: false,
                    nullable: true,
                    visible: true,
                    titleKey: "email",
                },
                status: {
                    name: "status",
                    type: "string",
                    editable: false,
                    nullable: true,
                    visible: true,
                    titleKey: "registers.lectors.status",
                },
                statusEn: {
                    name: "statusEn",
                    type: "string",
                    editable: false,
                    nullable: true,
                    visible: true,
                    titleKey: "registers.lectors.status",
                },
                thematicArea: {
                    name: "thematicArea",
                    type: "string",
                    editable: false,
                    nullable: true,
                    visible: true,
                    titleKey: "registers.lectors.thematicArea",
                },
                thematicAreaEn: {
                    name: "thematicAreaEn",
                    type: "string",
                    editable: false,
                    nullable: true,
                    visible: true,
                    titleKey: "registers.lectors.thematicArea",
                },
                institutionName: {
                    name: "institutionName",
                    type: "string",
                    editable: false,
                    nullable: true,
                    visible: true,
                    titleKey: "institution.name",
                },
                institutionNameEn: {
                    name: "institutionNameEn",
                    type: "string",
                    editable: false,
                    nullable: true,
                    visible: true,
                    titleKey: "institution.name",
                },
                institutionCity: {
                    name: "institutionCity",
                    type: "string",
                    editable: false,
                    nullable: true,
                    visible: true,
                    titleKey: "institution.name",
                },
                institutionCityEn: {
                    name: "institutionCityEn",
                    type: "string",
                    editable: false,
                    nullable: true,
                    visible: true,
                    titleKey: "institution.name",
                },
                institutionCountry: {
                    name: "institutionCountry",
                    type: "string",
                    editable: false,
                    nullable: true,
                    visible: true,
                    titleKey: "institution.name",
                },
                institutionCountryEn: {
                    name: "institutionCountryEn",
                    type: "string",
                    editable: false,
                    nullable: true,
                    visible: true,
                    titleKey: "institution.name",
                },
                mobilePhone: {
                    name: "mobilePhone",
                    type: "string",
                    editable: false,
                    nullable: true,
                    visible: true,
                    titleKey: "person.mobilePhone",
                },
                phone: {
                    name: "phone",
                    type: "string",
                    editable: false,
                    nullable: true,
                    visible: true,
                    titleKey: "person.phone",
                },
            },
            transportSettings: {
                read: {
                    url: `${store.getters.baseUrl}/api/lectors/listDeleted`,
                    dataType: "json",
                    type: "POST",
                    contentType: "application/json",
                    beforeSend: this.beforeSend,
                },
                parameterMap: this.parameterMap,
            },
        };
    },
    computed: {
        ...mapGetters(["claims", "isEnVersion", "gridOptions"]),
        token: function () {
            return store.getters.authToken;
        },
    },
    methods: {
        dataSourceError(e) {
            this.$vs.notify({
                title: this.$t("notifications.error"),
                text: this.$helpers.kendoDataSourceErrorText(e),
                color: "danger",
                time: this.$store.getters.notificationDuration,
            });
        },
        parameterMap: function (options, operation) {
            if (operation !== "read" && options.models) {
                return { models: kendo.stringify(options.models) };
            } else {
                return kendo.stringify(options);
            }
        },
        beforeSend: function (req) {
            if (this.token) {
                req.setRequestHeader("Authorization", "Bearer " + this.token);
            }
        },
        btnsTemplate: function (e) {
            return {
                template: Vue.component(BtnsTemplate.name, BtnsTemplate),
                templateArgs: {
                    ...e,
                    parentComponent: this.$refs.grid,
                    btns: [
                        {
                            name: "details",
                            text: "",
                            tooltip: this.$t("buttons.details.tooltipText"),
                            class: "btn btn-info",
                            icon: "fas fa-eye",
                            show: this.claims.lecturer.read,
                            clickHandler: this.detailsBtnClick,
                        },
                        {
                            name: "detailsInNewTab",
                            text: "",
                            tooltip: this.$t("buttons.detailsInNewTab.btnText"),
                            class: "btn btn-primary",
                            icon: "fas fa-eye",
                            show: this.claims.lecturer.read,
                            clickHandler: this.detailsInTewTabBtnClick,
                        },
                        {
                            name: "delete",
                            text: "",
                            tooltip: this.$t("buttons.undelete.tooltipText"),
                            class: "btn btn-success",
                            icon: "fas fa-check",
                            show: this.claims.deletedRecords.update,
                            clickHandler: this.undeleteBtnClick,
                        },
                    ],
                },
            };
        },
        refresh() {
            const grid = this.$refs.grid.kendoWidget();
            if (grid) {
                grid.dataSource.read();
            }
        },
        clearFilters() {
            this.searchQuery = "";
            const grid = this.$refs.grid.kendoWidget();
            if (grid) {
                grid.dataSource.sort({});
                grid.dataSource.page(1);
                grid.dataSource.aggregate([]);
                grid.dataSource.filter({});
            }
        },
        massSearch() {
            if (!this.searchQuery) {
                this.$vs.notify({
                    title: this.$t("buttons.search.btnText"),
                    text: this.$t("emptySearchFilter"),
                    color: "warning",
                    time: this.$store.getters.notificationDuration,
                });

                return;
            }

            let grid = this.$refs.grid.kendoWidget();
            if (grid) {
                let filter = this.$helpers.getMassFilterForGrid(
                    grid,
                    this.searchQuery,
                    this.excludedColumnsForSearch
                );
                if (filter) {
                    grid.dataSource.filter(filter);
                }
            }
        },
        detailsBtnClick(args) {
            this.$router.push("/registers/lectors/details/" + args.id);
        },
        detailsInTewTabBtnClick(args) {
            let routeData = this.$router.resolve({
                name: "lectorsDetails",
                params: { id: args.id },
                query: {
                    returnUrl: '/administration/deletedRecords/lecturers'
                }
            });
            window.open(routeData.href, "_blank");
        },
        undeleteBtnClick(args) {
            kendo
                .confirm(`${this.$t("buttons.undelete.confirmationText1", { name: this.isEnVersion ? args.fullNameEn : args.fullName })}`)
                .then(
                    function () {
                        this.undeleteLecturer(args.id);
                    }.bind(this)
                );
        },
        undeleteLecturer(id) {
            this.$http
                .delete("/api/lectors/undelete", { params: { id: id } })
                .then((response) => {
                    this.refresh();

                    this.$vs.notify({
                        title: "",
                        text: this.$helpers.responseMessage(response),
                        color: "success",
                        time: this.$store.getters.notificationDuration,
                    });
                })
                .catch((error) => {
                    this.$vs.notify({
                        title: this.$t(
                            "request.response.status." +
                                this.$helpers.errorStatusCode(error)
                        ),
                        text: this.$helpers.errorText(error),
                        color: "danger",
                        time: this.$store.getters.notificationDuration,
                    });
                });
        },
    },
};
</script>
